// components/social/LiveView.jsx
//
// The social worker's browser, inside PCake. The page is rendered by the worker at THIS screen's
// size (as a phone on a phone), shown 1:1 and refreshed about once a second; taps, scrolling and
// typing on the picture are played on the real page. Logging in, 2-step codes, security checks
// and WhatsApp's QR code are done here by the person — no external browser needed.
//
// Typing: a picture cannot receive text, so a hidden text box takes the keyboard. Tapping the
// page focuses it (that is what brings up a phone's keyboard), and every character, Backspace and
// Enter typed into it is sent to the field that was tapped on the real page.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaArrowLeft, FaRedo, FaTimes, FaCheck, FaKeyboard, FaPaste } from 'react-icons/fa';
import { sendInput, setSessionStatus, watchSession, platformMeta } from '../../services/socialService';
import './social.css';

const SPECIAL_KEYS = new Set([
  'Enter', 'Tab', 'Backspace', 'Delete', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Home', 'End', 'PageUp', 'PageDown',
]);

const primaryLabel = (session) => {
  if (session?.purpose === 'assist') return 'Done — posted';
  if (session?.purpose === 'connect') {
    if (session.platform === 'facebook') return 'Use this page';
    if (session.platform === 'whatsapp') return 'Use this chat';
    return 'Done';
  }
  return 'Continue';
};

export default function LiveView({ sessionId, uid, onClose }) {
  const [session, setSession] = useState(null);
  const [handing, setHanding] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const kbRef = useRef(null);
  const typed = useRef('');
  const typeTimer = useRef(null);
  const wheel = useRef({ dy: 0, x: 0, y: 0, timer: null });
  const touch = useRef(null);
  const closedRef = useRef(false);

  useEffect(() => watchSession(sessionId, setSession), [sessionId]);

  // The worker ended it (done / closed / failed): the view goes away by itself.
  useEffect(() => {
    if (!session || closedRef.current) return;
    if (['done', 'closed', 'failed'].includes(session.status)) {
      closedRef.current = true;
      onClose?.(session.status, session.message);
    }
    if (session.status === 'live') setHanding(false);
  }, [session, onClose]);

  const live = session?.status === 'live';

  const send = useCallback((kind, extra) => {
    sendInput(uid, sessionId, kind, extra).catch((e) => console.warn('live input not sent:', e.message));
  }, [uid, sessionId]);

  const flushTyping = useCallback(() => {
    if (typeTimer.current) { clearTimeout(typeTimer.current); typeTimer.current = null; }
    const text = typed.current;
    typed.current = '';
    if (text) send('type', { text });
  }, [send]);

  const queueText = useCallback((text) => {
    typed.current += text;
    if (typeTimer.current) clearTimeout(typeTimer.current);
    typeTimer.current = setTimeout(flushTyping, 250);
  }, [flushTyping]);

  const sendKey = useCallback((key) => { flushTyping(); send('key', { text: key }); }, [flushTyping, send]);

  /** Where on the real page a point on the picture is (CSS pixels of the worker's page). */
  const pagePoint = (clientX, clientY) => {
    const img = imgRef.current;
    if (!img || !session?.width || !session?.height) return null;
    const r = img.getBoundingClientRect();
    return {
      x: Math.round((clientX - r.left) * (session.width / r.width)),
      y: Math.round((clientY - r.top) * (session.height / r.height)),
    };
  };

  /** Focus the hidden box — on a phone this is what opens the keyboard (must run inside the tap). */
  const openKeyboard = () => {
    const el = kbRef.current;
    if (!el) return;
    el.value = '';
    el.focus({ preventScroll: true });
    setKbOpen(true);
  };

  // ── hidden keyboard box ────────────────────────────────────────────────────────────────
  // Phones send most keys as `input` events (key "Unidentified" on keydown), and Backspace on an
  // empty box only as `beforeinput` — so all three are read, and the box is emptied after each
  // character so it never fills up.
  useEffect(() => {
    const el = kbRef.current;
    if (!el) return undefined;
    const onBeforeInput = (e) => {
      if (!live) return;
      if (e.inputType === 'deleteContentBackward') { e.preventDefault(); sendKey('Backspace'); }
      else if (e.inputType === 'deleteContentForward') { e.preventDefault(); sendKey('Delete'); }
      else if (e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') { e.preventDefault(); sendKey('Enter'); }
    };
    const onInput = () => {
      const v = el.value;
      el.value = '';
      if (live && v) queueText(v);
    };
    const onKeyDown = (e) => {
      e.stopPropagation();
      if (!live) return;
      if (SPECIAL_KEYS.has(e.key) && e.key !== 'Backspace') {
        e.preventDefault();
        sendKey(e.key === 'Tab' && e.shiftKey ? 'Shift+Tab' : e.key);
      } else if (e.key === 'Backspace' && !el.value) {
        // Desktop browsers: Backspace on the empty box fires keydown but no beforeinput.
        e.preventDefault();
        sendKey('Backspace');
      }
    };
    const onPaste = (e) => {
      const text = e.clipboardData?.getData('text');
      if (live && text) { e.preventDefault(); flushTyping(); send('type', { text: text.slice(0, 2000) }); }
    };
    const onBlur = () => setKbOpen(false);
    el.addEventListener('beforeinput', onBeforeInput);
    el.addEventListener('input', onInput);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('paste', onPaste);
    el.addEventListener('blur', onBlur);
    return () => {
      el.removeEventListener('beforeinput', onBeforeInput);
      el.removeEventListener('input', onInput);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('paste', onPaste);
      el.removeEventListener('blur', onBlur);
    };
  }, [live, queueText, sendKey, flushTyping, send]);

  // ── pointer on the picture ─────────────────────────────────────────────────────────────
  const onTap = (clientX, clientY) => {
    const at = pagePoint(clientX, clientY);
    if (!at || !live || handing) return;
    flushTyping();
    send('click', at);
    openKeyboard();   // a tap usually means "type here"; harmless when it was a button
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, y0: t.clientY, moved: false };
  };
  const onTouchMove = (e) => {
    const t = e.touches[0];
    const s = touch.current;
    if (!s) return;
    if (Math.abs(t.clientY - s.y0) > 8) s.moved = true;
    if (s.moved && live) {
      // A drag on the picture scrolls the real page (finger up = content up).
      const dy = s.y - t.clientY;
      s.y = t.clientY;
      const w = wheel.current;
      w.dy += dy * (session?.height && imgRef.current ? session.height / imgRef.current.getBoundingClientRect().height : 1);
      const at = pagePoint(t.clientX, t.clientY);
      if (at) { w.x = at.x; w.y = at.y; }
      if (!w.timer) {
        w.timer = setTimeout(() => {
          const d = Math.round(w.dy);
          w.dy = 0; w.timer = null;
          if (d) send('scroll', { x: w.x, y: w.y, text: String(d) });
        }, 120);
      }
    }
  };
  const onTouchEnd = (e) => {
    const s = touch.current;
    touch.current = null;
    if (s && !s.moved) {
      e.preventDefault();   // no ghost mouse click afterwards
      onTap(s.x, s.y);
    }
  };

  const onWheel = (e) => {
    if (!live) return;
    const at = pagePoint(e.clientX, e.clientY);
    if (!at) return;
    const w = wheel.current;
    w.dy += e.deltaY; w.x = at.x; w.y = at.y;
    if (!w.timer) {
      w.timer = setTimeout(() => {
        const dy = Math.round(w.dy);
        w.dy = 0; w.timer = null;
        if (dy) send('scroll', { x: w.x, y: w.y, text: String(dy) });
      }, 200);
    }
  };

  // Desktop keyboard when the stage itself has focus (before the first click).
  const onStageKeyDown = (e) => {
    if (!live || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key.length === 1) { e.preventDefault(); queueText(e.key); return; }
    if (SPECIAL_KEYS.has(e.key)) { e.preventDefault(); sendKey(e.key === 'Tab' && e.shiftKey ? 'Shift+Tab' : e.key); }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text && live) { flushTyping(); send('type', { text: text.slice(0, 2000) }); }
    } catch { openKeyboard(); }
  };

  const handBack = async () => {
    flushTyping();
    setHanding(true);
    kbRef.current?.blur();
    await setSessionStatus(sessionId, 'continue').catch(() => setHanding(false));
  };

  const close = async () => {
    flushTyping();
    closedRef.current = true;
    await setSessionStatus(sessionId, 'closed').catch(() => undefined);
    onClose?.('closed');
  };

  const meta = platformMeta(session?.platform);
  const waiting = !session || session.status === 'starting';
  // Shown at the worker's CSS size (1:1) — never stretched wider, so text stays the real size.
  const frameStyle = session?.width ? { maxWidth: `${session.width}px` } : undefined;

  return createPortal(
    <div className={`social-live-overlay${session?.mobile ? ' is-mobile' : ''}`} role="dialog" aria-modal="true" aria-label="Live view">
      <div className="social-live-window">
        <div className="social-live-bar">
          <span className="social-live-badge" style={{ background: meta?.color || '#fa4848' }}>{meta?.short || '•'}</span>
          <span className="social-live-url">{session?.url || meta?.label || 'Opening…'}</span>
          <button type="button" className="social-btn" disabled={!live || handing} onClick={() => send('back')} title="Back" aria-label="Back"><FaArrowLeft /></button>
          <button type="button" className="social-btn" disabled={!live || handing} onClick={() => send('reload')} title="Reload" aria-label="Reload"><FaRedo /></button>
          <button type="button" className="social-btn social-btn--primary" disabled={!live || handing} onClick={handBack}>
            <FaCheck /> {handing ? 'Working…' : primaryLabel(session)}
          </button>
          <button type="button" className="social-btn social-btn--quiet" onClick={close} title="Close" aria-label="Close"><FaTimes /></button>
        </div>
        {session?.message && <div className="social-live-message">{session.message}</div>}
        <div
          ref={boxRef}
          tabIndex={0}
          className="social-live-stage"
          onKeyDown={onStageKeyDown}
          onWheel={onWheel}
        >
          {session?.frame ? (
            <img
              ref={imgRef}
              src={session.frame}
              alt={session.title || 'Live page'}
              draggable={false}
              style={frameStyle}
              className={`social-live-frame${live && !handing ? ' is-live' : ''}`}
              onClick={(e) => onTap(e.clientX, e.clientY)}
              onDoubleClick={(e) => { const at = pagePoint(e.clientX, e.clientY); if (at && live) send('dblclick', at); }}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            />
          ) : (
            <div className="social-live-wait">
              <span className="social-spinner" />
              <span>{waiting ? 'Opening the page in the PCake browser…' : 'Loading…'}</span>
            </div>
          )}
          {/* Takes the keyboard. 16px so iOS does not zoom; nearly invisible but not display:none
              (a hidden element cannot be focused, and then no keyboard appears). */}
          <input
            ref={kbRef}
            className="social-live-kb"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="go"
            aria-label="Type into the page"
          />
        </div>
        <div className="social-live-keys">
          <button type="button" className={`social-btn${kbOpen ? ' social-btn--primary' : ''}`} disabled={!live || handing} onClick={openKeyboard}>
            <FaKeyboard /> Keyboard
          </button>
          <button type="button" className="social-btn" disabled={!live || handing} onClick={() => sendKey('Backspace')} aria-label="Backspace">⌫</button>
          <button type="button" className="social-btn" disabled={!live || handing} onClick={() => sendKey('Tab')}>Tab</button>
          <button type="button" className="social-btn" disabled={!live || handing} onClick={() => sendKey('Enter')}>Enter ↵</button>
          <button type="button" className="social-btn" disabled={!live || handing} onClick={pasteFromClipboard} title="Paste"><FaPaste /></button>
          {session?.caption && (
            <button type="button" className="social-btn" disabled={!live || handing} onClick={() => send('type', { text: session.caption })} title="Type the post text where the cursor is">
              Type caption
            </button>
          )}
        </div>
        <div className="social-live-foot">
          Tap a field, then type — your keyboard writes into the page. Drag to scroll. Your login stays in PCake's own browser, so next time is one click.
        </div>
      </div>
    </div>,
    document.getElementById('portal-root') || document.body,
  );
}
